import { useEntityFilterStore, useWorkspaceSettings } from '@easy-expense/data-firestore-client';
import { EntityFilterSchema, Expense } from '@easy-expense/data-schema-v2';
import { getTranslation, useIntlStore } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import {
  getCombinedExpenseStatus,
  getCombinedExpenseStatusColor,
  getCombinedExpenseStatusLabel,
} from '@easy-expense/utils-client';
import { dateFormatSkeleton, EmptyEntityFilter } from '@easy-expense/utils-shared';
import { FixMe } from '@easy-expense/utils-typescript';
import { MenuItem, TextField } from '@mui/material';
import _ from 'lodash';
import {
  MRT_GlobalFilterTextField,
  useMaterialReactTable,
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_SortingState,
  MRT_TableOptions,
} from 'material-react-table';
import { MRT_FilterFn } from 'material-react-table';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { CategoryBadge } from './CategoryBadge.component';
import {
  ColumnVisibilityPopover,
  ColumnVisibilityPrefs,
  CustomizableColumnKey,
  DEFAULT_COLUMN_VISIBILITY,
} from './ColumnVisibilityPopover.component';
import { ExpenseWithData, Reports, Vendor } from './ExpenseRow.component';
import { ReceiptButton } from './ReceiptButton.component';
import { ReceiptSideModal } from './ReceiptSideModal.component';
import { useMappedExpenseData } from './helpers';
import { Range } from '../../components/Export/ExportDateFilter.component';
import { setExpenseNavOrder } from '../../hooks/useExpenseNavOrder';
import { usePersistentState } from '../../hooks/usePersistantState';
import {
  getResumeRow,
  resolveResumeRowPage,
  ResumeRowList,
  setResumeRow,
} from '../../lib/resumeRow';
import { Button } from '../Button.components';
import { ExportType } from '../Export/ExportDataType.component';
import { EntityFilters } from '../Filters/EntityFilters.component';
import { SortCreatedAt } from '../Filters/SortCreatedAt.component';
import { useConvertEntityFilterToColumnFilters } from '../Filters/helpers';
import { Modal } from '../Shared/Modal.component';
import { StatusBadge } from '../Shared/StatusBadge.component';
import { TableTextSize } from '../Shared/TableTextSize';

type ColumnFieldKeys =
  | 'vendorValueName'
  | 'date'
  | 'categoryValueName'
  | 'creatorDisplayName'
  | 'paymentMethodName'
  | 'reports'
  | 'total'
  | 'receipts'
  | 'transactionID'
  | 'desc'
  | 'approvalStatus';

interface MaterialExpenseTableProps {
  onRowClick: (expenseKey: string) => void;
  filter?: (e: Expense) => boolean;
  showBulk?: boolean;
  header?: React.ReactNode;
  filterUser?: string;
  selected?: string[];
  setSelected?: React.Dispatch<React.SetStateAction<string[]>>;
  isReport?: boolean;
  showSearch?: boolean;
  expenseList?: string[] | undefined;
  visibleColumns?: ColumnFieldKeys[] | undefined;
  additionalOptions?: Partial<MRT_TableOptions<ExpenseWithData>>;
  /** Renders the Date column without the year, e.g. "Jul 13" instead of "Jul 13, 2026". */
  hideDateYear?: boolean;
  /**
   * Opts this table into tracking a Resume Row under the given list, so it
   * re-opens on the page holding the row the user was last looking at. Omit for
   * tables embedded in a report or an approval flow, which always start at the
   * first page.
   */
  resumeRowList?: ResumeRowList;
}

const arrayIncludesSome: MRT_FilterFn<FixMe> = (row, columnId, filterValues) => {
  const cellValue = row.getValue(columnId) as string;
  return filterValues.includes(cellValue.toLowerCase());
};

export const ExpenseTable: React.FC<React.PropsWithChildren<MaterialExpenseTableProps>> = ({
  onRowClick,
  header,
  expenseList,
  visibleColumns,
  additionalOptions,
  hideDateYear,
  resumeRowList,
}) => {
  const { formatDate, formatCurrency } = useIntlStore();
  const navigate = useNavigate();
  const { workspaceSettings } = useWorkspaceSettings();

  const storeFilter = useEntityFilterStore();
  const [createdAtSortedFilter, setCreatedAtSortedFilter] = usePersistentState<
    'asc' | 'desc' | undefined
  >('createdAtSortedFilter');
  const [expenseSorting, setExpenseSorting] =
    usePersistentState<MRT_SortingState>('expenseSorting');
  const [expensePageSize, setExpensePageSize] = usePersistentState<number>('expensePageSize');
  const [columnVisibilityPrefs, setColumnVisibilityPrefs] =
    usePersistentState<ColumnVisibilityPrefs>('transactionsColumnVisibility');
  const [pageIndex, setPageIndex] = React.useState(0);
  const [columnsAnchorEl, setColumnsAnchorEl] = React.useState<HTMLElement | null>(null);

  // The rows in the order they are displayed, kept in a ref so the pagination
  // handler can name the row a page starts on without re-creating the handler
  // every time the data changes.
  const sortedRowKeysRef = React.useRef<string[]>([]);
  const restoredResumeRow = React.useRef(false);
  // Read during the first render, before any effect can run. Applying the column
  // filters below resets the page index, which reaches onPaginationChange and
  // moves the Resume Row, so reading it later would only ever find the row this
  // mount just overwrote it with.
  const rowToResumeOn = React.useRef(resumeRowList ? getResumeRow(resumeRowList) : undefined);

  const defaultSorting = React.useMemo((): MRT_SortingState => {
    if (expenseSorting) {
      return expenseSorting;
    }
    const sorting: MRT_SortingState = [{ desc: true, id: 'date' }];
    if (createdAtSortedFilter) {
      sorting.unshift({ desc: createdAtSortedFilter === 'desc', id: 'createdAt' });
    }
    return sorting;
  }, [expenseSorting, createdAtSortedFilter]);

  const excludedColumnKeys = React.useMemo(() => {
    const excluded: CustomizableColumnKey[] = [];
    if (!workspaceSettings?.showTransactionID) {
      excluded.push('transactionID');
    }
    if (!workspaceSettings?.requiresApproval && !workspaceSettings?.showReimbursementsToggle) {
      excluded.push('approvalStatus');
    }
    return excluded;
  }, [
    workspaceSettings?.showTransactionID,
    workspaceSettings?.requiresApproval,
    workspaceSettings?.showReimbursementsToggle,
  ]);

  const columnVisibility = React.useMemo(() => {
    const prefs = columnVisibilityPrefs ?? DEFAULT_COLUMN_VISIBILITY;
    const overrides: Partial<ColumnVisibilityPrefs> = {};
    for (const key of excludedColumnKeys) {
      overrides[key] = false;
    }
    return {
      isReimbursable: false,
      reimbursableState: false,
      createdAt: false,
      ...prefs,
      ...overrides,
    };
  }, [columnVisibilityPrefs, excludedColumnKeys]);

  const { setEntityFilter } = storeFilter;
  const convert = useConvertEntityFilterToColumnFilters();

  // Flipped once the stored filters have been pushed into the table. Set here
  // rather than in a ref so it lands in the same render as the filters
  // themselves, giving the Resume Row restore below a reliable signal that the
  // rows it is reading are the ones the user will actually see.
  const [storeFiltersApplied, setStoreFiltersApplied] = React.useState(false);
  const storeFilterHasChanged = React.useRef(false);

  React.useEffect(() => {
    const tableFilters = convert(storeFilter);
    table.setColumnFilters(tableFilters);
    setStoreFiltersApplied(true);
    // Changing a filter drops the user back to the first page, which is what
    // TanStack's automatic reset did before it was turned off. Skipped on mount,
    // where this effect only replays filters the user already had and must not
    // undo the Resume Row restore.
    if (storeFilterHasChanged.current) {
      setPageIndex(0);
    }
    storeFilterHasChanged.current = true;
  }, [storeFilter]);

  const activeFiltersCount = React.useMemo(() => {
    const activeFilters = Object.values(storeFilter).filter((filter) => filter !== null);
    return activeFilters.length - 1;
  }, [storeFilter]);

  const [showModal, setShowModal] = React.useState(false);
  const [modalReceipts, setModalReceipts] = React.useState<string[]>([]);

  function onReceiptButtonClick(receipts?: string[]) {
    if (!receipts?.length) {
      return;
    }
    setModalReceipts(receipts);
    setShowModal(true);
  }

  const renderFiltersFooter = (
    <Layout.Row
      style={{
        borderTop: `2px solid ${theme.colors.grayXLight}`,
        borderBottomLeftRadius: 10,
        borderBottomRightRadius: 10,
      }}
      bg="white"
      px={32}
      py={24}
      justify="flex-end"
    >
      <Button.Primary
        onClick={() => setShowFilters(false)}
        radius={50}
        style={{
          border: `2px solid ${theme.colors.white}`,
        }}
      >
        <OpenSans.Custom size={15} weight="bold-700" style={{ color: theme.colors.buttonWhite }}>
          {getTranslation('Apply Filters')}
        </OpenSans.Custom>
        <Icon
          size={15}
          color={theme.colors.buttonWhite}
          style={{ paddingLeft: 10 }}
          name="chevron-forward"
        />
      </Button.Primary>
    </Layout.Row>
  );

  const expensesWithData = useMappedExpenseData(expenseList);

  const columns = React.useMemo<MRT_ColumnDef<ExpenseWithData>[]>(() => {
    const columns: MRT_ColumnDef<ExpenseWithData>[] = [
      {
        accessorFn: (row) => row.createdAt,
        accessorKey: 'createdAt',
        enableGlobalFilter: true,
        header: '',
      },
      {
        accessorFn: (row) => row.isReimbursable,
        accessorKey: 'isReimbursable',
        enableGlobalFilter: true,
        header: '',
      },
      {
        accessorFn: (row) => row.reimbursableState,
        accessorKey: 'reimbursableState',
        enableGlobalFilter: true,
        filterFn: (row, _id, filterValue) => {
          return row.original.reimbursableState === filterValue;
        },
        header: '',
      },
      {
        accessorFn: (row) => row.date,
        accessorKey: 'date',
        id: 'date',
        filterFn: 'betweenInclusive',
        header: getTranslation('Date'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs}>{getTranslation('Date')}</OpenSans.Secondary>
        ),
        maxSize: 10,
        Cell: ({ row }) => {
          const date = row.original.date;
          return (
            <Layout.Column style={{ width: 80 }}>
              <OpenSans.Secondary size={TableTextSize.xs}>
                {formatDate(new Date(date), {
                  skeleton: hideDateYear
                    ? dateFormatSkeleton.abbreviatedNoYear
                    : dateFormatSkeleton.abbreviated,
                })}
              </OpenSans.Secondary>
            </Layout.Column>
          );
        },
      },
      {
        accessorKey: 'vendorValueName',
        header: getTranslation('To/From'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs}>
            {getTranslation('To/From')}
          </OpenSans.Secondary>
        ),
        size: 100,
        Cell: ({ row }) => {
          const vendorValue = row.original.vendorValue;
          return <Vendor vendor={vendorValue} />;
        },
      },
      {
        accessorKey: 'categoryValueName',
        header: getTranslation('Category'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs}>
            {getTranslation('Category')}
          </OpenSans.Secondary>
        ),
        maxSize: 40,
        Cell: ({ row }) => {
          const categoryValue = row.original.categoryValue;
          return <CategoryBadge category={categoryValue} />;
        },
      },
      {
        filterFn: arrayIncludesSome,
        accessorFn: (row) => row.creatorDisplayName,
        accessorKey: 'creatorDisplayName',
        header: getTranslation('Created By'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs}>
            {getTranslation('Created By')}
          </OpenSans.Secondary>
        ),
        size: 100,
        Cell: ({ row }) => {
          return (
            <Layout.Column>
              <OpenSans.Primary
                size={TableTextSize.xs}
                style={{
                  width: 100,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'inline-block',
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                }}
              >
                {row.original.creatorDisplayName}
              </OpenSans.Primary>
            </Layout.Column>
          );
        },
      },

      {
        accessorKey: 'paymentMethodName',
        header: getTranslation('Account'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs}>
            {getTranslation('Account')}
          </OpenSans.Secondary>
        ),
        size: 100,
        Cell: ({ renderedCellValue }) => {
          return (
            <OpenSans.Primary
              size={TableTextSize.xs}
              color={renderedCellValue ? undefined : 'secondary'}
            >
              {renderedCellValue ? renderedCellValue : 'None'}
            </OpenSans.Primary>
          );
        },
      },
      {
        accessorKey: 'reports',
        accessorFn: (row) => row.reports.map((r) => r.name).join(''),
        filterFn: (row, _id, filterValue) => {
          if (filterValue === 'No Report') {
            return row.original.reports.length === 0;
          }
          return row.original.reports.some((r) => r.name === filterValue);
        },
        header: getTranslation('Report'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs}>
            {getTranslation('Reports')}
          </OpenSans.Secondary>
        ),
        size: 100,
        Cell: ({ row }) => {
          const reports = row.original.reports;
          return (
            <Layout.Row>
              <Reports reports={reports} />
            </Layout.Row>
          );
        },
      },
      {
        accessorKey: 'approvalStatus',
        accessorFn: (row) => getCombinedExpenseStatus(row),
        header: getTranslation('Status'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs}>
            {getTranslation('Status')}
          </OpenSans.Secondary>
        ),
        size: 100,
        Cell: ({ row }) => {
          const status = getCombinedExpenseStatus(row.original);
          return (
            <StatusBadge
              color={getCombinedExpenseStatusColor(status)}
              text={getCombinedExpenseStatusLabel(status)}
            />
          );
        },
      },
      {
        accessorKey: 'total',
        header: getTranslation('Amount'),
        filterFn: 'between',
        muiTableHeadCellProps: {
          align: 'right',
        },
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs} style={{ paddingLeft: 6 }}>
            {getTranslation('Amount')}
          </OpenSans.Secondary>
        ),
        size: 50,
        Cell: ({ row }) => {
          const value = row.original.total;
          const [dollars, cents] = formatCurrency(-1 * value).split('.');
          return (
            <Layout.Column align="flex-end">
              <OpenSans.Primary
                style={{ whiteSpace: 'nowrap' }}
                size={TableTextSize.xs}
                color={value > 0 ? 'primary' : 'successDark'}
                weight="bold-700"
              >
                {dollars}
                <Layout.Column justify="flex-end">
                  <OpenSans.Primary
                    size={TableTextSize.xs}
                    color={value > 0 ? 'primary' : 'successDark'}
                    weight="bold-700"
                  >
                    .{cents}
                  </OpenSans.Primary>
                  <Spacer.Vertical size={1.5} />
                </Layout.Column>
              </OpenSans.Primary>
            </Layout.Column>
          );
        },
      },
      {
        accessorKey: 'receipts',
        header: getTranslation('Receipt'),
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs}>
            {getTranslation('Receipt')}
          </OpenSans.Secondary>
        ),
        Filter: (props) => {
          return (
            <TextField
              fullWidth
              margin="none"
              onChange={(e) => props.column.setFilterValue(e.target.value || undefined)}
              placeholder="Filter"
              select
              value={props.column.getFilterValue() ?? ''}
              variant="standard"
            >
              <MenuItem value="none">All</MenuItem>
              <MenuItem value="true">Has Receipt</MenuItem>
              <MenuItem value="false">No Receipt</MenuItem>
            </TextField>
          );
        },
        filterFn: (row, _id, filterValue) => {
          if (filterValue === 'none') {
            return true;
          }

          const isTrueSet = filterValue === 'true';
          return row.original.receipts.length > 0 === isTrueSet;
        },
        enableSorting: false,
        size: 10,
        Cell: ({ row }) => {
          const receipts = row.original.receipts;
          return (
            <Layout.Column align>
              <ReceiptButton
                receipt={receipts[0]}
                setModalOpen={() => onReceiptButtonClick(receipts)}
              />
            </Layout.Column>
          );
        },
      },
      {
        accessorKey: 'transactionID',
        header: getTranslation('Trans. #'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs}>
            {getTranslation('Trans. #')}
          </OpenSans.Secondary>
        ),
        size: 120,
        Cell: ({ row }) => {
          const transactionID = row.original.transactionID;
          return transactionID ? (
            <OpenSans.Primary size={TableTextSize.xs}>{transactionID}</OpenSans.Primary>
          ) : (
            <OpenSans.Secondary size={TableTextSize.xs}>{'—'}</OpenSans.Secondary>
          );
        },
      },
      {
        accessorKey: 'desc',
        header: getTranslation('Notes'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.xs}>{getTranslation('Notes')}</OpenSans.Secondary>
        ),
        size: 160,
        Cell: ({ row }) => {
          const desc = row.original.desc;
          return desc ? (
            <OpenSans.Primary
              size={TableTextSize.xs}
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'inline-block',
                whiteSpace: 'nowrap',
                maxWidth: 200,
              }}
            >
              {desc}
            </OpenSans.Primary>
          ) : (
            <OpenSans.Secondary size={TableTextSize.xs}>{'—'}</OpenSans.Secondary>
          );
        },
      },
    ];

    return columns.filter((c) => {
      return !visibleColumns || visibleColumns.includes(c.accessorKey as ColumnFieldKeys);
    });
  }, [hideDateYear]);

  const [showFilters, setShowFilters] = React.useState(false);

  const tableParams: MRT_TableOptions<ExpenseWithData> = {
    columns,
    data: expensesWithData,
    initialState: {
      showGlobalFilter: true,
      density: 'compact',
    },
    state: {
      sorting: defaultSorting,
      pagination: { pageIndex, pageSize: expensePageSize ?? 25 },
      columnVisibility,
    },
    // The page index is reset by hand below instead. TanStack resets it whenever
    // the row model is rebuilt, which on mount happens when the stored filters
    // are applied - after the Resume Row has been restored, so it would send the
    // user straight back to the first page and, because the reset arrives
    // through onPaginationChange, overwrite the Resume Row itself.
    autoResetPageIndex: false,
    onSortingChange: (updater) => {
      const prev = expenseSorting ?? [{ desc: true, id: 'date' }];
      const next = typeof updater === 'function' ? updater(prev) : updater;
      setExpenseSorting(next);
      setPageIndex(0);
    },
    onPaginationChange: (updater) => {
      const prev = { pageIndex, pageSize: expensePageSize ?? 25 };
      const next = typeof updater === 'function' ? updater(prev) : updater;
      setPageIndex(next.pageIndex);
      setExpensePageSize(next.pageSize);
      // Paging by hand moves the Resume Row to the top of the page landed on, so
      // leaving and coming back honours the move rather than undoing it. A page
      // with no row behind it yet means the rows are still loading, which is not
      // the user moving anywhere — leave the Resume Row alone.
      const rowStartingPage = sortedRowKeysRef.current[next.pageIndex * next.pageSize];
      if (resumeRowList && rowStartingPage) {
        setResumeRow(resumeRowList, rowStartingPage);
      }
    },

    icons: {
      CloseIcon: () => (
        <Icon
          name="close"
          size={18}
          color={theme.colors.secondary}
          style={{ padding: 0, margin: 0 }}
        />
      ),
      SearchIcon: () => (
        <Icon name="search" size={18} color={theme.colors.secondary} style={{ marginRight: 8 }} />
      ),
      FilterAltIcon: () => <Icon name="funnel" size={18} color={theme.colors.secondary} />,
      ViewColumnIcon: () => <Icon name="build-outline" size={18} color={theme.colors.secondary} />,
    },
    enableColumnActions: false,
    columnFilterDisplayMode: 'custom',
    getRowId: (row) => row.key,
    enableDensityToggle: false,
    muiTableHeadCellProps: {
      sx: {
        '& .Mui-TableHeadCell-Content': {
          color: theme.colors.secondary,
          fontWeight: 'regular',
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => onRowClick(row.id),
      sx: {
        cursor: 'pointer',
      },
    }),
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: '0',
        border: '',
      },
    },
    renderTopToolbar: ({ table }) => {
      const filteredRows = table.getPrePaginationRowModel().rows;
      const filteredExpenseTotal = React.useMemo(() => {
        return filteredRows.reduce((acc, curr) => acc + curr.original.total, 0);
      }, [filteredRows]);

      return (
        <Layout.Column>
          <Layout.Row py align>
            <MRT_GlobalFilterTextField
              table={table}
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: theme.colors.inputBackground,
                  borderRadius: 2,
                  width: 500,
                  paddingRight: 1,
                },
              }}
            />

            <Spacer.Horizontal size={16} />

            <Layout.PressableRow
              align
              bg="inputBackground"
              border={[1, 'solid', 'inputBorder']}
              px
              py={6}
              radius={100}
              onClick={() => {
                setShowFilters(true);
              }}
            >
              <Icon name="funnel-outline" size={16} color="brandPrimary" />
              <Spacer.Horizontal size={8} />
              <OpenSans.Primary>{getTranslation('Add Filter')}</OpenSans.Primary>
            </Layout.PressableRow>

            <Spacer.Horizontal size={8} />

            <Layout.PressableRow
              align
              bg="inputBackground"
              border={[1, 'solid', 'inputBorder']}
              px
              py={6}
              radius={100}
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                setColumnsAnchorEl(e.currentTarget);
              }}
            >
              <Icon name="options-outline" size={16} color="brandPrimary" />
              <Spacer.Horizontal size={8} />
              <OpenSans.Primary>{getTranslation('Columns')}</OpenSans.Primary>
            </Layout.PressableRow>

            <ColumnVisibilityPopover
              anchorEl={columnsAnchorEl}
              onClose={() => setColumnsAnchorEl(null)}
              visibility={columnVisibilityPrefs ?? DEFAULT_COLUMN_VISIBILITY}
              onVisibilityChange={(prefs: ColumnVisibilityPrefs) => setColumnVisibilityPrefs(prefs)}
              excludeKeys={excludedColumnKeys}
            />

            {table.getState().columnFilters.length > 0 ? (
              <Layout.Row align>
                <Spacer.Horizontal />

                <Layout.Column style={{ width: '1px', height: 32 }} bg="grayLight" />

                <Spacer.Horizontal />

                <Layout.PressableRow
                  align
                  bg="brandPrimaryXLight"
                  border={[1, 'solid', 'brandPrimary']}
                  px
                  py={6}
                  radius={100}
                  onClick={() => {
                    table.setColumnFilters([]);
                    setEntityFilter(EmptyEntityFilter);
                  }}
                >
                  <Icon name="close-outline" size={16} color={theme.colors.brandPrimary} />
                  <Spacer.Horizontal size={8} />
                  <OpenSans.Pressable>
                    {getTranslation('Clear') +
                      ' ' +
                      activeFiltersCount +
                      ' ' +
                      getTranslation('Filters')}
                  </OpenSans.Pressable>
                </Layout.PressableRow>
              </Layout.Row>
            ) : null}
          </Layout.Row>

          {activeFiltersCount > 0 ? (
            <Layout.Column px={8}>
              <Spacer.Vertical size={4} />
              <Layout.Row
                px={16}
                py
                border={[1, 'solid', 'grayXLight']}
                style={{
                  boxShadow: '0 0 5px 2px rgba(0, 0, 0, 0.1)',
                }}
                radius={8}
                align
              >
                <Layout.Column>
                  <OpenSans.Secondary size="s-16">
                    {getTranslation('Filter matches')}
                  </OpenSans.Secondary>
                  <OpenSans.Primary weight="bold-700">
                    {filteredRows.length} {getTranslation('transactions')}
                  </OpenSans.Primary>
                </Layout.Column>

                <Spacer.Horizontal size={32} />

                <Layout.Column>
                  <OpenSans.Secondary size="s-16">{getTranslation('Spend')}</OpenSans.Secondary>
                  <OpenSans.Primary weight="bold-700">
                    {formatCurrency(filteredExpenseTotal)}
                  </OpenSans.Primary>
                </Layout.Column>

                {/* TODO: Add incomes to transactions page  */}
                {/* <Spacer.Horizontal size={32} />

                <Layout.Column>
                  <OpenSans.Secondary size="s-16">Income</OpenSans.Secondary>
                  <OpenSans.Primary weight="bold-700">$0.00</OpenSans.Primary>
                </Layout.Column> */}

                <Spacer.Flex />

                {/* TODO: Add as part of https://linear.app/easy-expense/issue/EZ-1153/create-flow-of-add-all-to-report
                 <Button.Secondary
                  onClick={() => {
                    setShowFilters(true);
                  }}
                  content="Add All to Report"
                />

                <Spacer.Horizontal /> */}

                <Button.Primary
                  active={filteredRows.length > 0}
                  onClick={() => {
                    const filter = JSON.stringify(storeFilter);
                    navigate('/export', { state: { storeFilter: filter } });
                  }}
                  content="Export All"
                />
              </Layout.Row>
              <Spacer.Vertical size={20} />
            </Layout.Column>
          ) : null}
        </Layout.Column>
      );
    },
  };

  const completeTableParams = _.merge(tableParams, additionalOptions);

  const table = useMaterialReactTable(completeTableParams);

  // Persist the filtered + sorted order (across all pages) so the expense detail
  // page can step to the next/previous expense in the order shown here.
  const sortedRowKeys = table.getSortedRowModel().rows.map((row) => row.id);
  const sortedRowKeysSignature = sortedRowKeys.join(',');
  React.useEffect(() => {
    setExpenseNavOrder(sortedRowKeys);
  }, [sortedRowKeysSignature]);

  sortedRowKeysRef.current = sortedRowKeys;

  // Open on the page holding the Resume Row. Rows stream in from Firestore, so
  // this waits for the first non-empty order, and runs only once per mount so a
  // later background update cannot yank a user who has since paged elsewhere.
  // Waiting on the stored filters matters as much: the table starts every mount
  // unfiltered, and a page counted against the unfiltered order points well past
  // the end of a filtered one.
  React.useEffect(() => {
    if (!resumeRowList || restoredResumeRow.current || sortedRowKeys.length === 0) {
      return;
    }
    if (!storeFiltersApplied) {
      return;
    }
    restoredResumeRow.current = true;
    const resumePage = resolveResumeRowPage(
      rowToResumeOn.current,
      sortedRowKeys,
      expensePageSize ?? 25,
    );
    if (resumePage !== undefined) {
      setPageIndex(resumePage);
    }
  }, [sortedRowKeysSignature, storeFiltersApplied]);

  // Rows can fall away underneath the page being shown - a column filter set
  // from the table's own header, or another member archiving expenses. With the
  // automatic reset turned off, bring a page that has run past the end back to
  // the first one so the user never lands on an empty table. A list that is
  // still loading has no pages yet and is left alone.
  const lastPageIndex = Math.max(0, Math.ceil(sortedRowKeys.length / (expensePageSize ?? 25)) - 1);
  React.useEffect(() => {
    if (sortedRowKeys.length > 0 && pageIndex > lastPageIndex) {
      setPageIndex(0);
    }
  }, [lastPageIndex, pageIndex, sortedRowKeys.length]);

  const [dateRange, setDateRange] = React.useState<Range>('custom');

  return (
    <>
      <ReceiptSideModal
        modalOpen={showModal}
        setModalState={setShowModal}
        receipts={modalReceipts}
      />
      <Layout.Column>
        <Layout.Row>{header}</Layout.Row>

        <MaterialReactTable table={table} />

        <Modal showModal={showFilters} setShowModal={setShowFilters} footer={renderFiltersFooter}>
          <Layout.Column>
            <Spacer.Vertical size={16} />
            <SortCreatedAt
              initialSortingState={createdAtSortedFilter}
              setSorted={function (createdAt: 'asc' | 'desc' | undefined): void {
                if (!createdAt) {
                  table.setSorting([]);
                } else {
                  table.setSorting([{ desc: createdAt === 'desc', id: 'createdAt' }]);
                }
                setCreatedAtSortedFilter(createdAt);
              }}
            />
            <Spacer.Vertical size={16} />
            <EntityFilters
              filter={storeFilter}
              dataType={ExportType.transactions}
              setFilter={(filter) => {
                setEntityFilter(EntityFilterSchema.parse(filter));
              }}
              range={dateRange}
              setDateRange={setDateRange}
              hideIncomes={true}
            />
            <Spacer.Vertical size={16} />
          </Layout.Column>
        </Modal>
      </Layout.Column>
    </>
  );
};
